/**
 * sirus/Chat — R232q20 Phase 4.
 *
 * WoW 3.3.5a / Sirus chat box. Pinned bottom-left at 16/16 px from edges,
 * 380×220 px (resizable later). Supports per-tab message buffers, channel
 * colours, slash-command routing and shift-click item links.
 *
 * Tabs (default): Общий / Бой / Шёпот / Группа / Гильдия / Торговля.
 * Each tab carries its own ring buffer of `ChatMessage` (max 200) plus a
 * `channels: Set<string>` filter. Right-clicking a tab opens an inline
 * editor (rename / colour / channels-included). Edits persist via
 * localStorage `aiwari-chat-tabs`.
 *
 * Slash commands are parsed locally and dispatched as a window event
 * `aiwari-chat-send` with detail = { channel, message, target? }. The
 * networking layer in World.tsx subscribes and forwards to the realm room.
 * NOTE: this component is intentionally side-effect-free w.r.t. the real
 * Colyseus connection — keeps the Phase 4 UI in isolation. Real send wire
 * lives in Chat.legacy.tsx for now (see DEFERRED).
 *
 * DEFERRED:
 *   – Real send through Colyseus (`sendChatExtended`) — owner: World.tsx.
 *   – Item-link tooltip on hover: clicking an item-link string in the log
 *     should pop UnitTooltip (or new ItemTooltip).
 *   – Resize handles (top-right + bottom-right corners).
 */

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties } from 'react';

import { create } from 'zustand';

import { useUI } from '../../state/store.js';
import { useUiSettings } from '../../state/ui-settings-store.js';

/* ─────────────────────────────────────────────────────────────────
 * Tiny inline chat-store (kept local until a richer module is needed)
 * ──────────────────────────────────────────────────────────────── */

export type ChatChannel =
  | 'say'
  | 'yell'
  | 'whisper'
  | 'party'
  | 'guild'
  | 'trade'
  | 'combat'
  | 'system';

export interface ChatMessage {
  id: string;
  ts: number;
  channel: ChatChannel;
  fromName: string;
  body: string;
  /** Whisper target / item-link payload. */
  target?: string;
}

export interface ChatTab {
  id: string;
  label: string;
  /** Hex colour for the tab pill border + active text. */
  colour: string;
  /** Channels included in this tab's filter. */
  channels: ChatChannel[];
  /** Sticky / cannot-be-deleted (default tabs). */
  sticky?: boolean;
}

interface ChatStore {
  tabs: ChatTab[];
  buffers: Record<string, ChatMessage[]>;
  pushMessage: (msg: ChatMessage) => void;
  updateTab: (id: string, patch: Partial<ChatTab>) => void;
  resetTabs: () => void;
}

const TABS_KEY = 'aiwari-chat-tabs';

const CHANNEL_LABEL_RU: Record<ChatChannel, string> = {
  say: 'Общий',
  yell: 'Крик',
  whisper: 'Шёпот',
  party: 'Группа',
  guild: 'Гильдия',
  trade: 'Торговля',
  combat: 'Бой',
  system: 'Система',
};

const CHANNEL_COLOUR: Record<ChatChannel, string> = {
  say: '#FFFFFF',
  yell: '#FF6E40',
  whisper: '#FF82C3',
  party: '#5B9BD5',
  guild: '#3FCF6A',
  trade: '#FFB347',
  combat: '#FFD479',
  system: '#C9A96A',
};

const DEFAULT_TABS: ChatTab[] = [
  { id: 'general', label: 'Общий', colour: '#FFFFFF', channels: ['say', 'yell', 'system'], sticky: true },
  { id: 'combat', label: 'Бой', colour: CHANNEL_COLOUR.combat, channels: ['combat'], sticky: true },
  { id: 'whisper', label: 'Шёпот', colour: CHANNEL_COLOUR.whisper, channels: ['whisper'], sticky: true },
  { id: 'party', label: 'Группа', colour: CHANNEL_COLOUR.party, channels: ['party'], sticky: true },
  { id: 'guild', label: 'Гильдия', colour: CHANNEL_COLOUR.guild, channels: ['guild'], sticky: true },
  { id: 'trade', label: 'Торговля', colour: CHANNEL_COLOUR.trade, channels: ['trade'], sticky: true },
];

function loadTabs(): ChatTab[] {
  try {
    const raw = localStorage.getItem(TABS_KEY);
    if (!raw) return DEFAULT_TABS;
    const parsed = JSON.parse(raw) as ChatTab[];
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_TABS;
    return parsed;
  } catch {
    return DEFAULT_TABS;
  }
}

function saveTabs(tabs: ChatTab[]) {
  try {
    localStorage.setItem(TABS_KEY, JSON.stringify(tabs));
  } catch {
    /* localStorage quota / private mode — ignore. */
  }
}

export const useChat = create<ChatStore>((set, get) => ({
  tabs: loadTabs(),
  buffers: {},
  pushMessage: (msg) => {
    const tabs = get().tabs;
    const buffers = { ...get().buffers };
    for (const t of tabs) {
      if (!t.channels.includes(msg.channel)) continue;
      const cur = buffers[t.id] ?? [];
      const next = cur.length >= 200 ? cur.slice(cur.length - 199) : cur.slice();
      next.push(msg);
      buffers[t.id] = next;
    }
    set({ buffers });
  },
  updateTab: (id, patch) => {
    const tabs = get().tabs.map((t) => (t.id === id ? { ...t, ...patch } : t));
    saveTabs(tabs);
    set({ tabs });
  },
  resetTabs: () => {
    saveTabs(DEFAULT_TABS);
    set({ tabs: DEFAULT_TABS, buffers: {} });
  },
}));

/* ─────────────────────────────────────────────────────────────────
 * Slash-command parser
 * ──────────────────────────────────────────────────────────────── */

interface ParsedCommand {
  channel: ChatChannel;
  message: string;
  target?: string;
  /** Pure local emote (no network send needed). */
  localEmote?: string;
}

const EMOTE_NAMES = new Set([
  'dance', 'wave', 'sit', 'sleep', 'kneel', 'cheer',
  'laugh', 'cry', 'bow', 'salute', 'roll', 'jump',
]);

function parseCommand(raw: string): ParsedCommand | null {
  const text = raw.trim();
  if (!text) return null;
  if (text[0] !== '/') return { channel: 'say', message: text };

  // /w <name> <body> — whisper.
  const wMatch = text.match(/^\/(w|whisper|t|tell)\s+(\S+)\s+(.+)$/i);
  if (wMatch) {
    return { channel: 'whisper', target: wMatch[2], message: wMatch[3] };
  }

  const head = text.split(/\s+/)[0]!.toLowerCase();
  const body = text.slice(head.length).trimStart();

  if (head === '/say' || head === '/s') return { channel: 'say', message: body };
  if (head === '/yell' || head === '/y') return { channel: 'yell', message: body };
  if (head === '/p' || head === '/party') return { channel: 'party', message: body };
  if (head === '/g' || head === '/guild') return { channel: 'guild', message: body };
  if (head === '/raid' || head === '/ra') return { channel: 'party', message: body };
  if (head === '/trade') return { channel: 'trade', message: body };
  if (head === '/who') return { channel: 'system', message: '/who', localEmote: 'who' };

  // Emotes — strip the leading slash, check the dictionary.
  const emote = head.slice(1);
  if (EMOTE_NAMES.has(emote)) {
    return { channel: 'system', message: `* ${emote}`, localEmote: emote };
  }

  // Unknown slash command — fall through and post as say so the user sees it.
  return { channel: 'say', message: text };
}

/* ─────────────────────────────────────────────────────────────────
 * Item-link drop helper
 * ──────────────────────────────────────────────────────────────── */

interface ChatItemLinkEvent {
  itemId: number | string;
  name: string;
}

/* ─────────────────────────────────────────────────────────────────
 * Component
 * ──────────────────────────────────────────────────────────────── */

const PANEL_W = 380;
const PANEL_H = 220;
const TAB_HEIGHT = 22;
const INPUT_HEIGHT = 28;

function ChatImpl() {
  const stage = useUI((s) => s.stage);
  const chatFontSize = useUiSettings((s) => s.chatFontSize);
  const tabs = useChat((s) => s.tabs);
  const buffers = useChat((s) => s.buffers);
  const pushMessage = useChat((s) => s.pushMessage);
  const updateTab = useChat((s) => s.updateTab);

  const [activeTabId, setActiveTabId] = useState<string>(tabs[0]?.id ?? 'general');
  const [input, setInput] = useState('');
  const [editorTabId, setEditorTabId] = useState<string | null>(null);
  const [tailFollow, setTailFollow] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const logRef = useRef<HTMLDivElement>(null);

  const activeTab = useMemo(
    () => tabs.find((t) => t.id === activeTabId) ?? tabs[0] ?? DEFAULT_TABS[0]!,
    [tabs, activeTabId],
  );

  const visible = buffers[activeTab.id] ?? [];

  /* ── Listen for inbound messages from World/realm via custom event ── */
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<ChatMessage>;
      if (!ce.detail) return;
      const m: ChatMessage = {
        id: ce.detail.id ?? `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        ts: ce.detail.ts ?? Date.now(),
        channel: ce.detail.channel,
        fromName: ce.detail.fromName,
        body: ce.detail.body,
        target: ce.detail.target,
      };
      pushMessage(m);
    };
    window.addEventListener('aiwari-chat-incoming', handler);
    return () => window.removeEventListener('aiwari-chat-incoming', handler);
  }, [pushMessage]);

  /* ── Listen for shift-click item links ── */
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<ChatItemLinkEvent>;
      if (!ce.detail) return;
      const link = `[${ce.detail.name}](id:${ce.detail.itemId})`;
      setInput((cur) => (cur ? `${cur} ${link} ` : `${link} `));
      window.setTimeout(() => inputRef.current?.focus(), 0);
    };
    window.addEventListener('aiwari-chat-item-link', handler);
    return () => window.removeEventListener('aiwari-chat-item-link', handler);
  }, []);

  /* ── Tail-follow autoscroll on new messages ── */
  useEffect(() => {
    if (!tailFollow) return;
    const el = logRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [visible.length, tailFollow]);

  /* ── Detect manual scroll-up to disable tail-follow ── */
  const onLogScroll = useCallback(() => {
    const el = logRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 16;
    setTailFollow(atBottom);
  }, []);

  /* ── Submit handler ── */
  const submit = useCallback(() => {
    const parsed = parseCommand(input);
    if (!parsed) {
      setInput('');
      return;
    }
    if (!parsed.message.trim() && !parsed.localEmote) {
      setInput('');
      return;
    }

    // Local echo so the user always sees their own line.
    const meName = useUI.getState().draft.name || 'Игрок';
    pushMessage({
      id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      ts: Date.now(),
      channel: parsed.channel,
      fromName: meName,
      body: parsed.message,
      target: parsed.target,
    });

    // Dispatch network event — World.tsx forwards to Colyseus.
    window.dispatchEvent(
      new CustomEvent('aiwari-chat-send', {
        detail: {
          channel: parsed.channel,
          message: parsed.message,
          target: parsed.target,
        },
      }),
    );

    if (parsed.localEmote) {
      window.dispatchEvent(
        new CustomEvent('aiwari-emote', { detail: { name: parsed.localEmote } }),
      );
    }

    setInput('');
    setTailFollow(true);
  }, [input, pushMessage]);

  /* ── Global keyboard: Enter to focus input, Esc to blur ── */
  useEffect(() => {
    if (stage !== 'world') return;
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      const tag = tgt?.tagName;
      const inForm = tag === 'INPUT' || tag === 'TEXTAREA' || tgt?.isContentEditable;
      if (e.key === 'Enter' && !inForm) {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        setInput('');
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage]);

  if (stage !== 'world') return null;

  /* ── Mobile collapsed icon button ── */
  if (mobileOpen === false && typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse) and (max-width: 640px)').matches) {
    return (
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        style={{
          position: 'fixed',
          left: 16,
          bottom: 16,
          width: 44,
          height: 44,
          zIndex: 25,
          background: 'var(--aiwari-bg-card-solid, #161210)',
          border: '1px solid var(--aiwari-frame-edge, #5C4520)',
          color: 'var(--aiwari-gold-soft, #FFD479)',
          fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
          fontSize: 22,
          cursor: 'pointer',
        }}
        aria-label="Открыть чат"
      >
        ✉
      </button>
    );
  }

  /* ── Body line renderer ── */
  const renderLine = (m: ChatMessage) => {
    const colour = CHANNEL_COLOUR[m.channel];
    const tag = `[${CHANNEL_LABEL_RU[m.channel]}]`;
    return (
      <div
        key={m.id}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          padding: '1px 0',
          fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
          fontSize: chatFontSize,
          lineHeight: 1.35,
          color: 'var(--aiwari-fg, #E8E1DA)',
          wordBreak: 'break-word',
        }}
      >
        <span style={{ color: colour, fontSize: chatFontSize - 2, opacity: 0.85 }}>{tag}</span>
        <span style={{ color: 'var(--aiwari-gold-soft, #FFD479)', fontWeight: 600 }}>
          {m.fromName}
          {m.target ? ` ↪ ${m.target}` : ''}:
        </span>
        <span>{m.body}</span>
      </div>
    );
  };

  /* ── Channel prefix shown inside the input field ── */
  const prefixForActive = (): string => {
    const primary = activeTab.channels[0];
    if (!primary) return '';
    if (primary === 'guild') return '/g ';
    if (primary === 'party') return '/p ';
    if (primary === 'whisper') return '/w ';
    if (primary === 'trade') return '/trade ';
    if (primary === 'yell') return '/y ';
    return '';
  };

  const inputPlaceholder = `${prefixForActive()}сообщение...`;

  /* ── Tab editor popover ── */
  const renderTabEditor = (tab: ChatTab) => (
    <div
      style={{
        position: 'absolute',
        bottom: PANEL_H + 4,
        left: 0,
        zIndex: 26,
        width: 240,
        padding: 10,
        background: 'var(--aiwari-bg-card-solid, #161210)',
        border: '1px solid var(--aiwari-frame-edge, #5C4520)',
        boxShadow: '0 6px 20px rgba(0,0,0,0.7)',
        fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
        color: 'var(--aiwari-fg, #E8E1DA)',
      }}
    >
      <label style={{ display: 'block', fontSize: 11, marginBottom: 4, color: 'var(--aiwari-fg-muted)' }}>
        Имя вкладки
      </label>
      <input
        type="text"
        value={tab.label}
        onChange={(e) => updateTab(tab.id, { label: e.target.value })}
        style={{
          width: '100%',
          padding: '4px 6px',
          background: '#1F1813',
          color: 'var(--aiwari-fg)',
          border: '1px solid #3A2818',
          fontFamily: 'inherit',
          fontSize: 13,
          marginBottom: 8,
        }}
      />
      <label style={{ display: 'block', fontSize: 11, marginBottom: 4, color: 'var(--aiwari-fg-muted)' }}>
        Цвет
      </label>
      <input
        type="color"
        value={tab.colour}
        onChange={(e) => updateTab(tab.id, { colour: e.target.value })}
        style={{ width: 56, height: 22, padding: 0, border: 'none', background: 'transparent', marginBottom: 8 }}
      />
      <div style={{ fontSize: 11, color: 'var(--aiwari-fg-muted)', marginBottom: 4 }}>Каналы</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {(Object.keys(CHANNEL_LABEL_RU) as ChatChannel[]).map((c) => {
          const on = tab.channels.includes(c);
          return (
            <label
              key={c}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                fontSize: 11,
                cursor: 'pointer',
                padding: '2px 4px',
                background: on ? 'rgba(201,169,106,0.15)' : 'transparent',
                border: '1px solid #3A2818',
              }}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => {
                  const next = on ? tab.channels.filter((x) => x !== c) : [...tab.channels, c];
                  updateTab(tab.id, { channels: next });
                }}
                style={{ accentColor: '#C9A96A' }}
              />
              <span style={{ color: CHANNEL_COLOUR[c] }}>{CHANNEL_LABEL_RU[c]}</span>
            </label>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => setEditorTabId(null)}
        style={{
          marginTop: 10,
          padding: '4px 10px',
          background: '#1F1813',
          color: 'var(--aiwari-gold-soft, #FFD479)',
          border: '1px solid #5C4520',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Готово
      </button>
    </div>
  );

  /* ── Root container ── */
  const rootStyle: CSSProperties = {
    position: 'fixed',
    left: 16,
    bottom: 16,
    width: PANEL_W,
    height: PANEL_H,
    zIndex: 25,
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--aiwari-bg-card, hsl(12 13% 8% / 0.92))',
    border: '1px solid var(--aiwari-frame-edge, #5C4520)',
    borderRadius: 'var(--aiwari-frame-radius, 4px)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    color: 'var(--aiwari-fg, #E8E1DA)',
    fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
    pointerEvents: 'auto',
  };

  return (
    <div className="aiwari-chat" style={rootStyle}>
      {/* ── Tabs row ──────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          gap: 2,
          padding: '4px 4px 0 4px',
          height: TAB_HEIGHT,
          borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
        }}
      >
        {tabs.map((t) => {
          const active = t.id === activeTab.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTabId(t.id)}
              onContextMenu={(e) => {
                e.preventDefault();
                setEditorTabId(t.id);
              }}
              style={{
                padding: '0 8px',
                height: TAB_HEIGHT - 4,
                background: active ? 'rgba(201,169,106,0.18)' : 'transparent',
                border: `1px solid ${active ? t.colour : '#3A2818'}`,
                borderBottom: active ? '1px solid transparent' : `1px solid #3A2818`,
                color: active ? t.colour : 'var(--aiwari-fg-muted, #B19B7E)',
                fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
                fontSize: 11,
                lineHeight: `${TAB_HEIGHT - 6}px`,
                cursor: 'pointer',
                userSelect: 'none',
              }}
              title="ЛКМ — выбрать вкладку\nПКМ — настроить"
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Tab editor popover ─────────────────────────────── */}
      {editorTabId && tabs.find((t) => t.id === editorTabId)
        ? renderTabEditor(tabs.find((t) => t.id === editorTabId)!)
        : null}

      {/* ── Message log ─────────────────────────────────────── */}
      <div
        ref={logRef}
        onScroll={onLogScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '6px 8px',
          fontSize: chatFontSize,
        }}
      >
        {visible.length === 0 ? (
          <div
            style={{
              color: 'var(--aiwari-fg-dim, #5C4520)',
              fontStyle: 'italic',
              fontSize: chatFontSize - 1,
              padding: '4px 0',
            }}
          >
            Чат пуст. Жми Enter, чтобы написать.
          </div>
        ) : (
          visible.map(renderLine)
        )}
      </div>

      {/* ── Input row ───────────────────────────────────────── */}
      <div
        style={{
          height: INPUT_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '0 4px',
          borderTop: '1px solid var(--aiwari-frame-edge, #5C4520)',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
            fontSize: 11,
            color: activeTab.colour,
            paddingLeft: 4,
            whiteSpace: 'nowrap',
          }}
        >
          {`[${activeTab.label}]`}
        </span>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={inputPlaceholder}
          maxLength={400}
          style={{
            flex: 1,
            height: INPUT_HEIGHT - 8,
            padding: '0 6px',
            background: '#1A140F',
            color: 'var(--aiwari-fg, #E8E1DA)',
            border: '1px solid #3A2818',
            borderRadius: 2,
            fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
            fontSize: chatFontSize,
            outline: 'none',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--aiwari-gold, #FAB300)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = '#3A2818';
          }}
        />
      </div>
    </div>
  );
}

/**
 * R232q86zdb (Wave 2 #1): React.memo wrapper.
 *
 * Chat doesn't subscribe to net-store.players, but its parent SirusHud
 * re-renders on every stage tick. Chat takes zero props; the memo with
 * the default `Object.is` comparison short-circuits the entire render
 * when its zustand subscriptions (useChat / useUI / useUiSettings)
 * haven't fired.
 */
export const Chat = memo(ChatImpl);

export default Chat;
